import { Button, FormSwitch, FullScreenLoading, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { updateProjectMemberNotifications as apiUpdatePrefs } from '~/api/preferences';
import { getProject as apiGetProject } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Member } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import {
  resolveNotificationsGate,
  useConflictNotificationsFlag,
  useConflictsChangeNotificationsFlag,
} from '~/hooks/useConflictNotificationsEnabled';
import rootStore from '~/store';

type TFormValues = Required<Member['notifications']['intelligence']>;

// placeholder to make react-hook-form happy; real values come from the member record
const defaultValues: TFormValues = {
  newConflict: false,
  clearedConflict: false,
  conflictsChanged: false,
};

// Members added before `conflictsChanged` existed lack the key; the server treats missing as on.
function toFormValues(prefs: Member['notifications']['intelligence']): TFormValues {
  return { ...prefs, conflictsChanged: prefs.conflictsChanged ?? true };
}

export function TabDetails() {
  const { resolved, enabled } = useConflictNotificationsFlag();
  const { enabled: conflictsChangedEnabled } = useConflictsChangeNotificationsFlag();
  const [isMember, setIsMember] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { me } = useSnapshot(rootStore) as typeof rootStore;
  const form = useForm<TFormValues>({ defaultValues });

  const query = useQuery({
    staleTime: 0,
    gcTime: 0,
    enabled: !!projectId && resolved && enabled,
    queryKey: ['user-project-preferences', 'notifications-intelligence', { projectId }],
    queryFn: async () => {
      const res = await apiGetProject(projectId);
      const member = res.members.find((m) => m.user.id === me?.id);
      if (member) {
        form.reset(toFormValues(member.notifications.intelligence));
        setIsMember(true);
      } else {
        setIsMember(false);
      }
      return res;
    },
  });

  const mutation = useMutation({
    mutationKey: ['user-project-preferences', 'notifications-intelligence', { projectId }],
    mutationFn: async (updates: Partial<Member['notifications']>) => apiUpdatePrefs(projectId, updates),
    onSuccess: (data) => {
      const prefs = toFormValues(data.notifications.intelligence);
      form.reset(prefs);
      capturePosthogEvent(POSTHOG_EVENTS.intelligenceNotificationPreferencesUpdated, {
        project_id: projectId,
        new_conflict: prefs.newConflict,
        cleared_conflict: prefs.clearedConflict,
        conflicts_changed: prefs.conflictsChanged,
      });
      Notify.info({
        title: 'Notification Preferences',
        message: 'Your notification preferences have been updated for this project',
      });
    },
  });

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    mutation.mutate({ intelligence: data });
  };

  const isLoading = query.isFetching;
  const isProcessing = mutation.isPending;
  const isDisabled = !form.formState.isDirty || isLoading || isProcessing;

  const gate = resolveNotificationsGate(resolved, enabled);
  if (gate === 'loading') return <FullScreenLoading />;
  if (gate === 'redirect')
    return <Navigate to="/projects/$projectId/$tabId" params={{ projectId, tabId: 'intelligence' }} />;

  if (isLoading) return <SkeletonSpinnerCentered />;
  if (query.isError) {
    return (
      <div>
        An error occurred while fetching your notification preferences, please <EmailLink>contact support</EmailLink>.
      </div>
    );
  }

  if (!isMember) {
    return (
      <div>
        You are not a member of this project. To receive notifications, join the{' '}
        <Link
          to="/projects/$projectId/$tabId"
          params={{ projectId, tabId: 'team' }}
          className="text-blue-50 underline hover:text-blue-60"
        >
          project
        </Link>
        .
      </div>
    );
  }

  function renderItem(name: keyof TFormValues, title: string, description: string) {
    return (
      <FormSwitch
        control={form.control}
        name={name}
        label={<span className="text-base font-medium text-secondary">{title}</span>}
        description={<span className="text-sm text-primary">{description}</span>}
        disabled={isProcessing}
        data-testid={`toggle-intelligence-${name}`}
      />
    );
  }

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      {renderItem(
        'newConflict',
        'New conflicts detected',
        'When updated data introduces a new conflict on this project',
      )}
      {conflictsChangedEnabled &&
        renderItem(
          'conflictsChanged',
          'Conflicts changed',
          'A weekly summary when existing conflicts on this project change',
        )}

      {mutation.isError && (
        <div className="text-sm text-red-60" data-testid="intelligence-notifications-save-error">
          Something went wrong saving your preferences. Please try again or <EmailLink>contact support</EmailLink>.
        </div>
      )}

      <Button
        type="submit"
        disabled={isDisabled}
        loading={isProcessing}
        data-testid="button-save-intelligence-notifications"
      >
        Save Preferences
      </Button>
      <div className="text-sm">
        <b>Note:</b> These preferences apply only to this project.
      </div>
    </Form>
  );
}
